/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { forwardRef, PropsWithChildren } from 'react'
import { pxToVw } from 'src/functions/units'
// import { pxToVw } from 'src/functions/units'

import styled, { css } from 'styled-components'
import { Nullable } from '../../types/main'
import { Color } from '../../types/theme'

type Props = PropsWithChildren<{
  altText?: string
  borderColor?: Color
  circular?: boolean
  className?: string
  image?: Nullable<string>
  size?: number | string
  transparentBase?: boolean
}>

const Avatar = forwardRef<HTMLDivElement, Props>(({
  altText = '',
  borderColor,
  circular = true,
  className,
  image,
  size = 24,
  transparentBase = false,
  children
}: Props, ref) => {
  return (
    <StyledAvatar
      ref={ref}
      className={className}
      $size={size}
      $circular={circular}
      $borderColor={borderColor}
      $transparentBase={transparentBase}
    >
      {image ? <img src={image} alt={altText} /> : <>{children}</>}
    </StyledAvatar>
  )
})

export default Avatar

const StyledAvatar = styled.div<{
  $borderColor?: Color
  $circular: boolean
  $size: number | string
  $transparentBase: boolean
}>`
  flex: 0 0 auto;
  width: ${({ $size }) => typeof $size === 'number' ? pxToVw($size) : $size};
  height: ${({ $size }) => typeof $size === 'number' ? pxToVw($size) : $size};
  background-color: ${({ theme, $transparentBase }) =>
    !$transparentBase ? theme.colors.gray200 : 'transparent'};
  overflow: hidden;

  ${({ $circular, $size }) =>
    $circular && $size
      ? css`
          border-radius: ${typeof $size === 'number' ? pxToVw($size / 2) : `calc(${$size} / 2)`};
        `
      : undefined}
  ${({ $borderColor }) =>
    $borderColor
      ? css`
          border: solid 1px ${$borderColor};
        `
      : undefined}
  
  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  ${({ theme, $size }) => theme.mediaQuery.mobile`
    width: ${typeof $size === 'number' ? pxToVw($size, 720) : undefined};
    height: ${typeof $size === 'number' ? pxToVw($size, 720) : undefined};
  `}
`
